import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
} from "@nexus/ui";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { useUpdateReportMarket } from "../../hooks/useUpdateReportMarket";
import { usePreflightMarkets } from "../../hooks/usePreflightMarkets";
import { preventDialogDismissForDropdown } from "../../lib/prevent-dialog-dismiss-for-dropdown";
import type { PreflightReportSummary } from "../../types/preflight.types";
import { MarketSelect } from "../MarketSelect/MarketSelect";

export interface ChangeReportMarketDialogProps {
  report: Pick<PreflightReportSummary, "id" | "market">;
  /** Custom trigger; defaults to a ghost "Change market" button. */
  trigger?: React.ReactNode;
  /** Controlled open state when the parent owns the trigger (e.g. header chip). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Permission-gated dialog to reassign a report's market from the tenant list
 * (same selector as create). Optimistically patches detail + list caches and
 * adjusts market report counts; rolls back on error.
 */
export function ChangeReportMarketDialog({
  report,
  trigger,
  open: controlledOpen,
  onOpenChange,
}: ChangeReportMarketDialogProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const [marketId, setMarketId] = useState<string | null>(
    report.market?.id ?? null,
  );
  const { markets, isPending: areMarketsPending } = usePreflightMarkets();
  const mutation = useUpdateReportMarket(report);

  useEffect(() => {
    if (open) setMarketId(report.market?.id ?? null);
  }, [open, report.market?.id]);

  if (!permissions.PREFLIGHT_RENAME_REPORT.isOn) return null;

  const selected = markets.find((market) => market.id === marketId);
  const canSubmit =
    selected != null && selected.id !== (report.market?.id ?? null);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger !== null ? (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button type="button" variant="ghost" size="sm">
              {t("preflight.changeMarket.trigger", "Change market")}
            </Button>
          )}
        </DialogTrigger>
      ) : null}
      <DialogContent
        className="top-16 max-w-sm translate-y-0 sm:top-20 sm:max-w-sm"
        onPointerDownOutside={preventDialogDismissForDropdown}
        onInteractOutside={preventDialogDismissForDropdown}
      >
        <DialogHeader>
          <DialogTitle>
            {t("preflight.changeMarket.title", "Change report market")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "preflight.changeMarket.description",
              "Pick another market from your organisation's list. Insights update to match.",
            )}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit && selected) {
              mutation.mutate(selected, { onSuccess: () => setOpen(false) });
            }
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-2">
            <Label>
              {t("preflight.changeMarket.label", "Market")}
            </Label>
            <MarketSelect
              markets={markets}
              value={marketId}
              onChange={setMarketId}
              mode="single"
              size="default"
              disabled={areMarketsPending || mutation.isPending}
              className="w-full max-w-none"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              {t("preflight.changeMarket.cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={!canSubmit || mutation.isPending}>
              {mutation.isPending
                ? t("preflight.changeMarket.saving", "Saving…")
                : t("preflight.changeMarket.save", "Save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
